/**
 * Risk Score Color Utilities
 *
 * Maps numerical risk scores to color schemes for the UI.
 *
 * Risk levels:
 *   score >= 100  -> High risk (red)
 *   score >= 30   -> Medium risk (amber/yellow)
 *   score >= 0    -> Low risk (green)
 *   score < 0     -> Trusted/Compliant (blue)
 */

/**
 * Get the base color name for a risk score.
 * @param {number} score - Numerical risk score
 * @returns {'red' | 'amber' | 'green' | 'blue'} Color name
 */
export function getRiskColor(score) {
  if (score == null) return 'gray';
  if (score >= 100) return 'red';
  if (score >= 30) return 'amber';
  if (score >= 0) return 'green';
  return 'blue';
}

/**
 * Get a human-readable label for a risk score.
 * @param {number} score - Numerical risk score
 * @returns {string} Risk level label
 */
export function getRiskLabel(score) {
  if (score == null) return 'Unknown';
  if (score >= 100) return 'High';
  if (score >= 30) return 'Medium';
  if (score >= 0) return 'Low';
  return 'Trusted';
}

/**
 * Get Tailwind background class for a risk score badge.
 * @param {number} score - Numerical risk score
 * @returns {string} Tailwind CSS class string for background + text color
 */
export function getRiskBgClass(score) {
  if (score == null) return 'bg-gray-700 text-gray-300';
  if (score >= 100) return 'bg-red-900/60 text-red-300 border border-red-700/50';
  if (score >= 30) return 'bg-amber-900/60 text-amber-300 border border-amber-700/50';
  if (score >= 0) return 'bg-green-900/60 text-green-300 border border-green-700/50';
  return 'bg-blue-900/60 text-blue-300 border border-blue-700/50';
}

/**
 * Get Tailwind text class for a risk score.
 * @param {number} score - Numerical risk score
 * @returns {string} Tailwind CSS text color class
 */
export function getRiskTextClass(score) {
  if (score == null) return 'text-gray-400';
  if (score >= 100) return 'text-red-400';
  if (score >= 30) return 'text-amber-400';
  if (score >= 0) return 'text-green-400';
  return 'text-blue-400';
}

// --- Classification circle (the portal-wide "click to classify" control) ---
//
// One shape, one palette, four tones — rendered by `ui/StatusCircle` and used in
// the LEADING Status column of every table that lets an analyst classify a row
// (IP Addresses, URL Clicks, AAD Session IDs, and the Attacker Window session
// list). Before v1.139.0 there were three different controls — a filled circle,
// a labelled pill and a text chip — for the same gesture.
//
// The vocabularies differ per domain (safe/suspect/malicious vs
// confirmed/suspected/benign vs safe/compromised), so each keeps its own
// status→tone mapper and they all land on this palette. Green always means the
// analyst cleared it, red always means they condemned it.
export const STATUS_CIRCLE_TONE = {
  green: 'bg-green-500 border-green-400',
  amber: 'bg-amber-500 border-amber-400',
  red:   'bg-red-500 border-red-400',
  gray:  'bg-gray-600 border-gray-500',
};

export function ipStatusTone(status) {
  switch (status) {
    case 'safe': return 'green';
    case 'suspect': return 'amber';
    case 'malicious': return 'red';
    default: return 'gray';
  }
}

export function urlStatusTone(status) {
  switch (status) {
    case 'benign': return 'green';
    case 'suspected': return 'amber';
    case 'confirmed': return 'red';
    default: return 'gray';
  }
}

export function sessionStatusTone(status) {
  switch (status) {
    case 'safe': return 'green';
    case 'compromised': return 'red';
    default: return 'gray';
  }
}

// --- IP Status Classification ---
// Cycle order: default → safe → suspect → malicious → default
export const IP_STATUSES = ['default', 'safe', 'suspect', 'malicious'];

export function getNextIPStatus(current) {
  const idx = IP_STATUSES.indexOf(current || 'default');
  return IP_STATUSES[(idx + 1) % IP_STATUSES.length];
}

export function getIPStatusTextClass(status) {
  switch (status) {
    case 'safe': return 'text-green-400';
    case 'suspect': return 'text-amber-400';
    case 'malicious': return 'text-red-400';
    default: return 'text-gray-200';
  }
}

export function getIPStatusBorderClass(status) {
  switch (status) {
    case 'safe': return 'border-l-4 border-l-blue-500';
    case 'suspect': return 'border-l-4 border-l-amber-500';
    case 'malicious': return 'border-l-4 border-l-red-500';
    default: return null; // no override — fall back to risk-based border
  }
}

export function getIPStatusBgClass(status) {
  switch (status) {
    case 'safe': return 'bg-green-900/60 text-green-300 border border-green-700/50';
    case 'suspect': return 'bg-amber-900/60 text-amber-300 border border-amber-700/50';
    case 'malicious': return 'bg-red-900/60 text-red-300 border border-red-700/50';
    default: return 'bg-gray-700/60 text-gray-400 border border-gray-600/50';
  }
}

export function getIPStatusLabel(status) {
  switch (status) {
    case 'safe': return 'Safe';
    case 'suspect': return 'Suspect';
    case 'malicious': return 'Malicious';
    default: return '';
  }
}

export function getRiskBorderClass(riskLevel) {
  if (typeof riskLevel === 'string') {
    switch (riskLevel.toLowerCase()) {
      case 'high':
        return 'border-l-4 border-l-red-500';
      case 'medium':
        return 'border-l-4 border-l-amber-500';
      case 'low':
        return 'border-l-4 border-l-green-500';
      case 'trusted':
      case 'known-good':
        return 'border-l-4 border-l-blue-500';
      default:
        return 'border-l-4 border-l-gray-600';
    }
  }
  // Numeric score
  if (riskLevel == null) return 'border-l-4 border-l-gray-600';
  if (riskLevel >= 100) return 'border-l-4 border-l-red-500';
  if (riskLevel >= 30) return 'border-l-4 border-l-amber-500';
  if (riskLevel >= 0) return 'border-l-4 border-l-green-500';
  return 'border-l-4 border-l-blue-500';
}

// --- URL Click Classification ---
// Cycle order: default → confirmed → suspected → benign → default
export const URL_STATUSES = ['default', 'confirmed', 'suspected', 'benign'];

export function getNextUrlStatus(current) {
  const idx = URL_STATUSES.indexOf(current || 'default');
  return URL_STATUSES[(idx + 1) % URL_STATUSES.length];
}

export function getUrlStatusTextClass(status) {
  switch (status) {
    case 'confirmed': return 'text-red-400';
    case 'suspected': return 'text-amber-400';
    case 'benign': return 'text-green-400';
    default: return 'text-gray-300';
  }
}

export function getUrlStatusBgClass(status) {
  switch (status) {
    case 'confirmed': return 'bg-red-900/60 text-red-300 border border-red-700/50';
    case 'suspected': return 'bg-amber-900/60 text-amber-300 border border-amber-700/50';
    case 'benign': return 'bg-green-900/60 text-green-300 border border-green-700/50';
    default: return 'bg-gray-700/60 text-gray-400 border border-gray-600/50';
  }
}

export function getUrlStatusLabel(status) {
  switch (status) {
    case 'confirmed': return 'Confirmed';
    case 'suspected': return 'Suspected';
    case 'benign': return 'Benign';
    default: return '';
  }
}
